import React, { useRef, useMemo, useState } from 'react';
import { useIntl, getLocale } from 'umi';
import {
  Card,
  Segment,
  // Switch,
  // Text,
} from 'tea-component';
import Abnormal from '@home/components/Abnormal';
import ArrowdownText from '@intelligence/components/ArrowdownText';
import ChartIconType from '@intelligence/components/ChartsComponents/ChartIconType';
import VisualisContent from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/components/Visualis/Content';
import { isEmpty } from '@intelligence/v3/utils/common';
import { ChartType } from '@intelligence/pages/TopCharts/_config/enums';
import { mobileDetailsOverlapSegOptions } from '@intelligence/pages/TopCharts/_config/metricOptions';
import { SORTABLE_METRICS_METADATAS_MAPPING } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/constants/metrics';

import type { IBaseProps, IDateType } from '@intelligence/types/common';
import type { IChartsIndexInstance } from '@intelligence/components/ChartsComponents/ChartsIndex';
import type {
  IMobileDetailsOverlapRank,
  IMobileDetailsOverlapRanksFetchParams,
  IMobileDetailsOverlapTrend,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/types';

interface IMobileDetailsOverlapVisualisProps extends IBaseProps {
  tableLoading: boolean;
  visualisLoading: boolean;
  chartType: ChartType;
  handleChartType: (payload: ChartType) => void;
  isAggregate: boolean;
  handleIsAggregateChange: (payload: boolean) => void;
  topChartsType: string;
  imageType?: any;
  dateType: IDateType;
  tableData: IMobileDetailsOverlapRank[];
  lineData: IMobileDetailsOverlapTrend[];
  fetchParams: IMobileDetailsOverlapRanksFetchParams;
  sortByName: string;
  sortByKey: string;
}

const isBarNoData = (list?: any[] | null): boolean => {
  if (isEmpty(list)) {
    return true;
  }

  const filteredList = list!.filter((item) => {
    const metrics = item?.metric_list || [];

    const filteredMetrics = metrics?.filter((metric: any) => {
      const metricKey = metric?.metric;
      const metricValue = metric?.value;
      const metricNumValue = Number.parseFloat(metricValue);

      return !!metricKey && !Number.isNaN(metricNumValue);
    });

    return !!filteredMetrics?.length;
  });

  return !filteredList?.length;
};

const isLineNoData = (list?: any[] | null): boolean => {
  if (isEmpty(list)) {
    return true;
  }

  const filteredList = list!.filter((item) => {
    const histories = item?.trend_metric_list || [];

    const filteredHistories = histories?.filter((metric: any) => {
      const historyTime = metric?.time;
      const historyMetrics = metric?.metric_value_list;
      const filteredHistoryMetrics = historyMetrics?.filter((historyMetric: any) => {
        const historyMetricKey = historyMetric?.metric;
        const historyMetricValue = historyMetric?.value;
        const historyMetricNumValue = Number.parseFloat(historyMetricValue);

        return !!historyMetricKey && !Number.isNaN(historyMetricNumValue);
      });

      return !!historyTime && !!filteredHistoryMetrics?.length;
    });

    return !!filteredHistories?.length;
  });

  return !filteredList?.length;
};

const MobileDetailsOverlapVisualis: React.FC<IMobileDetailsOverlapVisualisProps> = (props) => {
  const {
    tableLoading,
    visualisLoading,
    chartType,
    handleChartType,
    isAggregate,
    // handleIsAggregateChange,
    topChartsType,
    imageType = 'mobile',
    dateType = 'month',
    tableData,
    lineData,
    fetchParams,
    sortByName,
    sortByKey,
  } = props;

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const cardRef = useRef(null);

  const chartsInstance = useRef<IChartsIndexInstance>(null);

  const [metricSegVal, setMetricSegVal] = useState(mobileDetailsOverlapSegOptions?.[0]?.value || '');

  const [chartLoading, setChartLoading] = useState(false);

  const loading = tableLoading || visualisLoading;

  const isNoData = useMemo<boolean>(() => {
    if (loading) {
      return false;
    }

    if (chartType === ChartType.Bar) {
      return isBarNoData(tableData);
    }

    if (chartType === ChartType.Line) {
      return isLineNoData(lineData);
    }

    return false;
  }, [loading, chartType, tableData, lineData]);

  const sortByNameValue = useMemo<string>(() => {
    const metric = SORTABLE_METRICS_METADATAS_MAPPING[sortByKey];
    if (!metric?.name) return sortByName;
    return formatMessage({ id: metric.name }) || '';
  }, [locale, sortByKey, sortByName]);

  const renderOperation = () => {
    if (tableLoading) return null;
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <ChartIconType iconList={['bar', 'line']} chartType={chartType} handleChartType={handleChartType} />
        {/* REMARK: 这里仅隐藏入口，逻辑代码先保留，以免后续有需要开放功能入口 */}
        {/* {chartType === ChartType.Bar && (*/}
        {/*  <Switch*/}
        {/*    style={{ marginLeft: 10 }}*/}
        {/*    className="dbi-common-switcher"*/}
        {/*    value={isAggregate}*/}
        {/*    onChange={handleIsAggregateChange}*/}
        {/*  >*/}
        {/*    <Text>{formatMessage({ id: 'dbi_label_aggregated' })}</Text>*/}
        {/*  </Switch>*/}
        {/* )}*/}
      </div>
    );
  };

  const renderVisualisContext = () => {
    return (
      <Abnormal
        style={{ height: 410 }}
        isLoading={loading}
        isNoData={!loading && isNoData}
        componentLoading={loading}
        component={
          <>
            <div className="card-flex">
              <div className="card-title">
                {/* 指标筛选器 */}
                <Segment
                  className="dbi-pcGame-segment-box"
                  value={metricSegVal}
                  onChange={(value) => {
                    setMetricSegVal(value);
                  }}
                  options={mobileDetailsOverlapSegOptions?.map((t: any) => {
                    return {
                      ...(t || {}),
                      text: formatMessage({ id: t?.text }),
                    };
                  })}
                />
              </div>
            </div>
            <div className="card-line" ref={cardRef}>
              <VisualisContent
                imageType={imageType}
                topChartsType={topChartsType}
                cardRef={cardRef}
                chartsInstance={chartsInstance}
                metricSegVal={metricSegVal}
                chartType={chartType}
                tableData={tableData}
                lineData={lineData}
                dateType={dateType}
                fetchParams={fetchParams}
                chartLoading={chartLoading}
                setChartLoading={setChartLoading}
                isAggregate={isAggregate}
              />
            </div>
          </>
        }
      />
    );
  };

  return (
    <Card className="dbi-mobileGames-card-context">
      <Card.Body
        title={
          <div style={{ display: 'flex' }}>
            <span style={{ marginRight: 10 }}>{formatMessage({ id: 'dbi_label_trend%' })}</span>
            <ArrowdownText isNew={true} sortedData={sortByNameValue} order={fetchParams?.order} sortByKey={sortByKey} />
          </div>
        }
        operation={renderOperation()}
      >
        {renderVisualisContext()}
      </Card.Body>
    </Card>
  );
};

export default React.memo(MobileDetailsOverlapVisualis);
